import { ReactElement } from 'react';
import { IProductCardProps } from '.';
import { PlayButton, PurchaseButton } from '@components/common';
import { useSiteLeadStore } from '@store/siteLeadStore';
import { NavLink } from 'react-router-dom';
import { onImageError } from '@helper/utils';
import { masterFeedbackQuestions } from '@config/feedbackQuestions';
import { NO_DATA_FOUND, NO_JHAROKHA_PRODUCT_FOUND } from '@config/constant';
import { ToastType, useTosterStore } from '@store/toster';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { useFetchProductQuery } from '@services/hooks/jharokha/useGetJharokhaProduct';
import { useMutateJharokhaToken } from '@services/hooks/jharokha/useGetJharokhaTokenMutation';
import { ProfileData } from '@services/hooks/profile';
import { useGetRxDb } from '@hooks/getRxdbData';
import { useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';

export const ProductCard = (props: IProductCardProps): ReactElement => {
	const { product, productIndex, action, formik, hideLmsInfo, isDefaultProductRecommendation } = props;
	const { reason, glue, grade, setFeedbackModal, setProductIndex, setFeedbackResponseValue, setProductId } =
		useSiteLeadStore();
	const showCompetitionTag = productIndex === 0 && glue === 'Competition';
	const { mutate } = useFetchProductQuery();
	const { mutate: fetchJharokhaToken } = useMutateJharokhaToken();

	const showPremiumizationTag =
		productIndex === 0 && glue === 'FV' && grade && ['sh', 'marine'].includes(grade?.toLowerCase());

	const jarokhaToolURL = process.env.REACT_APP_JHAROKHA_TOOL_URL;
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const { setToaster } = useTosterStore();
	const { setFullPageLoader } = useSiderToggleMenu();

	const handlePurchase = (val: string): void => {
		const recordingStore = useSiteVisitRecordingStore.getState();
		if (recordingStore.phase === 'paused') {
			recordingStore.notifyPausedFormEdit();
		}

		const skipFeedback =
			(product.recommended_from === 'analytics' && !product?.questions) ||
			(product.recommended_from === 'master' &&
				reason &&
				masterFeedbackQuestions[reason]?.some((item) => !item.question)) ||
			(product.recommended_from === 'master' &&
				!product?.questions &&
				glue &&
				grade &&
				masterFeedbackQuestions[glue.toLowerCase()]?.some((item) => !item.question) &&
				(grade.toLowerCase() === 'sh' || grade.toLowerCase() === 'marine'));

		if (skipFeedback) {
			setFeedbackModal(false);
			formik?.setFieldValue(`productResponse[${productIndex}]`, val);
			return;
		}

		setProductIndex(productIndex);
		setFeedbackResponseValue(val);
		// Feedback dialog uses showModal() and would cover the pause reminder.
		if (recordingStore.phase === 'paused') {
			return;
		}
		setFeedbackModal(true);
	};

	const handleActivityClick = (): void => {
		setProductId(product?.id);
	};

	const handleJharokhaProductRedirection = async (product_code: string): Promise<void> => {
		try {
			setFullPageLoader(true);
			fetchJharokhaToken(undefined, {
				onSuccess: (jharokhaToken) => {
					if (jharokhaToken?.data?.token) {
						mutate(
							{
								productCode: product_code,
								token: jharokhaToken.data.token,
							},
							{
								onSuccess: (data) => {
									setFullPageLoader(false);
									if (data?.data?.url) {
										window.open(
											`${jarokhaToolURL}/${data.data.url}?ph=${profile[0]?.phone_number_c}&mpowertoken=${jharokhaToken?.data?.token}`,
											'_blank'
										);
										return;
									}
									setToaster(true, ToastType.error, NO_JHAROKHA_PRODUCT_FOUND);
								},
								onError: () => {
									setFullPageLoader(false);
									setToaster(true, ToastType.error, NO_DATA_FOUND);
								},
							}
						);
					}
				},
				onError: () => {
					setFullPageLoader(false);
					setToaster(true, ToastType.error, NO_DATA_FOUND);
				},
			});
		} catch (error) {
			setFullPageLoader(false);
			setToaster(true, ToastType.error, NO_DATA_FOUND);
		}
	};

	return (
		<li className="d-col d-col-2">
			<div className="product-discussion-card" id="product-popup">
				<div className="product-image jharokha-link">
					<img
						className={isDefaultProductRecommendation ? 'default-product-image' : ''}
						role="presentation"
						src={product?.image ?? ''}
						alt="fevicol"
						title="fevicol"
						width="104"
						height="156"
						onError={onImageError}
					/>
					<PlayButton product_code={product?.product_code} />
					<button
						type="button"
						className="btn btn-primary jharokha-btn"
						onClick={() => handleJharokhaProductRedirection(product?.product_code)}
					>
						Product Details
					</button>
				</div>

				<div className="product-details">
					<div>
						<span className="product-title">
							<span className="p-title h3">{product?.product_name}</span>{' '}
							<span className="tag-main-wrapper">
								<span className="p-tag">{product?.product_to_be ? 'Discuss' : 'Demo'}</span>
								{showCompetitionTag && <span className="competition-tag">Competition</span>}
								{showPremiumizationTag && <span className="competition-tag">Premiumization</span>}
							</span>
						</span>

						{!hideLmsInfo && (
							<div className="date-details">
								<span>
									Last Date of Demo: {product?.demo_date && product?.demo_date !== 'null' ? product?.demo_date : 'N/A'}
								</span>
								<span>
									Last Date of Purchase:{' '}
									{product?.discussed_date && product?.discussed_date !== 'null' ? product?.discussed_date : 'N/A'}
								</span>
							</div>
						)}

						{!isDefaultProductRecommendation && (
							<NavLink
								to="/activity-module"
								target="_blank"
								aria-label="activity module url"
								className="activity-module"
								onClick={handleActivityClick}
							>
								<span className="icon-module icon"></span>
								<span className="module-name">Activity Module</span>
							</NavLink>
						)}
					</div>

					{!hideLmsInfo && !isDefaultProductRecommendation && (
						<div className="discussion-wrapper">
							<span className="discussion-title">Have you done a discussion?</span>

							<span className="purchas-btn-wrapper">
								<PurchaseButton
									label="Yes"
									icon="icon-thumbs-up"
									handleClick={() => handlePurchase('True')}
									isActive={action === 'True'}
								/>
								<PurchaseButton
									label="No"
									icon="icon-thumbs-down"
									handleClick={() => handlePurchase('False')}
									isActive={action === 'False'}
								/>
							</span>
						</div>
					)}
				</div>
			</div>
		</li>
	);
};
